import React from "react";
import { Card, CardHeader, CardContent, Box, Typography, Divider, TextField, MenuItem } from "@material-ui/core";
import LaunchIcon from "@material-ui/icons/Launch";
import { useHistory } from "react-router-dom";

const currencyFmt = (v, code = "USD") =>
  new Intl.NumberFormat(undefined, { style: "currency", currency: code, maximumFractionDigits: 0 }).format(v || 0);

const formatDate = (value) => {
  if (!value) return "Unknown date";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(+date)) return "Unknown date";
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

const formatPeriodLabel = (value, grouping) => {
  if (!value) return "Unknown period";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(+date)) return "Unknown period";
  if (grouping === "month") {
    return date.toLocaleDateString(undefined, { month: "long", year: "numeric" });
  }
  if (grouping === "week") return `Week of ${formatDate(date)}`;
  return formatDate(date);
};

const getEntryPeriod = (invoice) =>
  invoice.accrualDate || invoice.period || invoice.latestPaymentDate || invoice.latestPaymentTs || null;

export default function PaymentList({
  title = "Displayed Payments",
  entries = [],
  currency = "USD",
  maxInvoices = 6,
  mode = "cash",
  groupByPeriod = false,
  grouping = "day",
}) {
  const history = useHistory();
  const items = React.useMemo(() => entries.slice(0, maxInvoices), [entries, maxInvoices]);
  const [selectedPeriod, setSelectedPeriod] = React.useState("all");
  const isCash = mode === "cash";
  const totalPayments = isCash ? entries.reduce((sum, invoice) => sum + (invoice.payments?.length || 0), 0) : 0;
  const totalAccrualAmount = !isCash ? entries.reduce((sum, invoice) => sum + (Number(invoice.amount) || 0), 0) : 0;
  const subheader = entries.length
    ? isCash
      ? `${entries.length} invoice${entries.length === 1 ? "" : "s"} • ${totalPayments} payment${totalPayments === 1 ? "" : "s"}`
      : `${entries.length} invoice${entries.length === 1 ? "" : "s"} • ${currencyFmt(totalAccrualAmount, currency)}`
    : undefined;
  const groups = React.useMemo(() => {
    if (!groupByPeriod) return [{ key: "all", label: "", items }];
    const grouped = [];
    const byKey = new Map();
    items.forEach((invoice) => {
      const period = getEntryPeriod(invoice);
      const date = period ? new Date(period) : null;
      const key = date && !Number.isNaN(+date) ? String(date.getTime()) : "unknown";
      if (!byKey.has(key)) {
        const group = {
          key,
          label: key === "unknown" ? "Unknown period" : formatPeriodLabel(date, grouping),
          items: [],
        };
        byKey.set(key, group);
        grouped.push(group);
      }
      byKey.get(key).items.push(invoice);
    });
    return grouped;
  }, [groupByPeriod, grouping, items]);

  React.useEffect(() => {
    if (selectedPeriod === "all") return;
    if (!groups.some((group) => group.key === selectedPeriod)) {
      setSelectedPeriod("all");
    }
  }, [groups, selectedPeriod]);

  const visibleGroups = selectedPeriod === "all"
    ? groups
    : groups.filter((group) => group.key === selectedPeriod);

  const renderInvoice = (invoice, idx, groupSize) => (
    <Box
      key={`${invoice.invoiceId || "invoice"}-${invoice.accrualDate || invoice.latestPaymentTs || idx}-${idx}`}
      mb={idx === groupSize - 1 ? 0 : 2}
      onClick={() => invoice.invoiceId && history.push(`/invoice/${invoice.invoiceId}`)}
      style={{ cursor: invoice.invoiceId ? "pointer" : "default" }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
          {invoice.invoiceNumber ? `Invoice #${invoice.invoiceNumber}` : '' || invoice.clientStore || invoice.clientName || "Invoice"}
        </Typography>
        {invoice.invoiceId && (
          <Box display="flex" alignItems="center" style={{ gap: 4 }}>
            <Typography variant="caption" color="primary">
              View invoice
            </Typography>
            <LaunchIcon fontSize="small" color="primary" />
          </Box>
        )}
      </Box>
      {isCash ? (
        invoice.payments?.slice(0, 5).map((pay, payIdx) => (
          <Box
            key={`${invoice.invoiceId || idx}-pay-${payIdx}`}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mt={0.5}
          >
            <Box>
              <Typography variant="body2">{currencyFmt(pay.amount, currency)}</Typography>
              <Typography variant="caption" color="textSecondary">
                {formatDate(pay.date)}{pay.paymentMethod ? ` • ${pay.paymentMethod}` : ""}
              </Typography>
            </Box>
            {pay.note && (
              <Typography variant="caption" color="textSecondary" style={{ marginLeft: 8, textAlign: "right" }}>
                {pay.note}
              </Typography>
            )}
          </Box>
        ))
      ) : (
        <Box mt={0.5} display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="body2" style={{ fontWeight: 600 }}>
              {currencyFmt(invoice.amount, currency)}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {formatDate(invoice.accrualDate)}
            </Typography>
          </Box>
        </Box>
      )}
      {idx !== groupSize - 1 && <Divider style={{ marginTop: 8 }} />}
    </Box>
  );

  return (
    <Card variant="outlined" style={{ height: "100%" }}>
      <CardHeader title={title} subheader={subheader} />
      <CardContent style={{ maxHeight: 300, overflowY: "auto" }}>
        {groupByPeriod && groups.length > 1 && (
          <Box mb={2}>
            <TextField
              select
              fullWidth
              size="small"
              variant="outlined"
              label="Period"
              value={selectedPeriod}
              onChange={(event) => setSelectedPeriod(event.target.value)}
            >
              <MenuItem value="all">All periods</MenuItem>
              {groups.map((group) => (
                <MenuItem key={group.key} value={group.key}>
                  {group.label} ({group.items.length})
                </MenuItem>
              ))}
            </TextField>
          </Box>
        )}
        {items.length === 0 ? (
          <Typography variant="body2" color="textSecondary">
            No activity in this range.
          </Typography>
        ) : (
          visibleGroups.map((group, groupIdx) => (
            <Box key={group.key} mb={groupIdx === visibleGroups.length - 1 ? 0 : 2}>
              {groupByPeriod && (
                <Box
                  mb={1}
                  px={1.25}
                  py={0.75}
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  style={{
                    background: "#f3f4f6",
                    border: "1px solid #d1d5db",
                    borderRadius: 6,
                  }}
                >
                  <Typography variant="subtitle2" style={{ fontWeight: 700, color: "#111827" }}>
                    {group.label}
                  </Typography>
                  <Typography variant="caption" style={{ fontWeight: 700, color: "#4b5563" }}>
                    {group.items.length} invoice{group.items.length === 1 ? "" : "s"}
                  </Typography>
                </Box>
              )}
              {group.items.map((invoice, idx) => renderInvoice(invoice, idx, group.items.length))}
            </Box>
          ))
        )}
      </CardContent>
    </Card>
  );
}
